import type { Icon } from '@phosphor-icons/react';
import {
  Buildings,
  CalendarCheck,
  ChatCircleText,
  ChatsCircle,
  Sliders,
  ChartLine,
  ClipboardText,
  Coins,
  Crown,
  Gift,
  IdentificationCard,
  ListBullets,
  Megaphone,
  Newspaper,
  ShareNetwork,
  ShieldCheck,
  UserFocus,
  Users,
} from '@phosphor-icons/react';

/**
 * Điều hướng chính, bám đúng thanh bên trong `design/ui-prototypes/reski-admin-web.html`
 * (màn `a-dash`): 4 nhóm, 11 mục, nhãn tiếng Việt lấy nguyên văn từ prototype.
 *
 * `a-member` và `a-queuecfg` không nằm ở đây vì trong prototype chúng là màn con,
 * vào từ Thành viên và Hàng chờ.
 */
export interface NavItem {
  /** Screen id trong 10-ui-screens.md — giữ để đối chiếu prototype khi dựng màn thật */
  screenId: string;
  path: string;
  label: string;
  icon: Icon;

  /**
   * Tính năng đã HOÃN khỏi Phase 1 — mục vẫn hiện nhưng mờ, không bấm được, có chú thích.
   *
   * <p>Vì sao không xóa hẳn: prototype giữ lại màn `a-queue`/`a-queuecfg` cho Phase 2, và
   * người dùng thử trong đội đã quen thấy mục đó. Xóa im lặng thì họ tưởng hỏng và đi báo lỗi.
   * Để nguyên mà bấm được thì họ vào một màn sẽ không bao giờ có nội dung. Mờ kèm lý do là
   * cách duy nhất trả lời được câu "cái này đâu rồi" ngay tại chỗ.</p>
   */
  heldReason?: string;

  /**
   * Chỉ Super Admin thấy. CRM hội sở không lọc được theo chi nhánh (ADR-005) nên mở cho
   * quản lý chi nhánh là mở toàn bộ danh bạ khách.
   */
  superAdminOnly?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const navGroups: NavGroup[] = [
  {
    label: 'Theo dõi',
    items: [
      { screenId: 'a-dash', path: '/', label: 'Bảng điều khiển', icon: ChartLine },
      {
        screenId: 'a-queue',
        path: '/queue',
        label: 'Hàng chờ',
        icon: ListBullets,
        heldReason: 'Bốc số hoãn khỏi Phase 1 (họp 16/08). Màn giữ lại cho Phase 2.',
      },
    ],
  },
  {
    label: 'Dữ liệu',
    items: [
      { screenId: 'a-branches', path: '/catalog', label: 'Chuỗi & chi nhánh', icon: Buildings },
      // Prototype không có màn này (Admin Web miễn prototype — ADR-009). Dựng theo khuôn
      // `a-branches`/`a-sysconfig` (bảng + modal). Đặt cạnh chi nhánh vì khung giờ đặt bàn là
      // dữ liệu vận hành gắn theo từng chi nhánh, không phải rule kinh doanh toàn hệ thống.
      { screenId: 'a-slots', path: '/reservation-slots', label: 'Khung giờ đặt bàn', icon: CalendarCheck },
      { screenId: 'a-crm', path: '/members', label: 'Thành viên', icon: Users, superAdminOnly: true },
      // Prototype không có màn quản lý nhân viên. Dựng theo đúng khuôn `a-branches`
      // (bảng + modal) để không đẻ ra ngôn ngữ thị giác mới — ghi lại ở CHANGELOG.
      { screenId: 'a-staff', path: '/staff', label: 'Nhân viên', icon: IdentificationCard },
    ],
  },
  {
    label: 'Cấu hình',
    items: [
      { screenId: 'a-earning', path: '/earning', label: 'Quy tắc tích điểm', icon: Coins },
      { screenId: 'a-tier', path: '/tiers', label: 'Hạng thẻ', icon: Crown },
      { screenId: 'a-reward', path: '/rewards', label: 'Quà & voucher', icon: Gift },
      { screenId: 'a-campaign', path: '/campaigns', label: 'Khuyến mãi', icon: Megaphone },
      // Prototype dùng `i-list` cho mục này, nhưng `ListBullets` đã thuộc *Hàng chờ* —
      // hai mục cùng icon thì thanh bên không còn phân biệt được bằng mắt.
      { screenId: 'a-content', path: '/contents', label: 'Nội dung & tin tức', icon: Newspaper },
      // Prototype không có màn này (Admin Web miễn prototype — ADR-009). Danh sách nhóm Zalo
      // không gắn chuỗi, chỉ super admin sửa — cùng khuôn bảng + modal của các màn cấu hình.
      {
        screenId: 'a-care-groups',
        path: '/customer-care-groups',
        label: 'Nhóm chăm sóc khách hàng',
        icon: ChatsCircle,
        superAdminOnly: true,
      },
      // Prototype không có màn này. Dựng theo đúng khuôn `a-branches` (bảng + modal)
      // để không đẻ ra ngôn ngữ thị giác mới — ghi lại ở CHANGELOG.
      { screenId: 'a-sysconfig', path: '/system-config', label: 'Thông số vận hành', icon: Sliders },
      // Link cửa hàng là chuỗi — không nhét vào Thông số vận hành (value chỉ chứa số).
      {
        screenId: 'a-app-links',
        path: '/app-links',
        label: 'Link cửa hàng ứng dụng',
        icon: ShareNetwork,
      },
    ],
  },
  {
    label: 'Kiểm soát',
    items: [
      { screenId: 'a-recon', path: '/reconciliation', label: 'Đối soát', icon: ClipboardText },
      // Đánh giá bữa ăn (M13). Prototype `a-review` có nhưng Admin Web miễn prototype (ADR-009) —
      // dựng theo khuôn `DataTable` + modal như các màn admin khác.
      { screenId: 'a-review', path: '/reviews', label: 'Đánh giá bữa ăn', icon: ChatCircleText },
      // Prototype không có màn này. Dựng theo khuôn `a-branches`/`a-sysconfig`.
      { screenId: 'a-privacy', path: '/privacy-requests', label: 'Yêu cầu dữ liệu', icon: UserFocus },
      { screenId: 'a-audit', path: '/audit', label: 'Nhật ký', icon: ShieldCheck },
    ],
  },
];

export const navItems: NavItem[] = navGroups.flatMap((group) => group.items);
